import { SessionStatus } from '@heidi/schema/ml-scribe';

/**
 * Single source of truth for session review workflow ordering. Mirrors the
 * backend `SessionStatus` ordering (`ml-scribe` `SESSION_STATUS_SORT_ORDER`).
 *
 * Every status ordering in the app — selectable/filterable display lists, the
 * list sort-rank maps, and the Action Center progress arc — derives from these
 * constants so there is no hand-maintained order to drift out of sync.
 *
 * `EMPTY` ("No status") is the sentinel: it leads the display lists but sinks to
 * the bottom of sort order.
 */

/**
 * Canonical display order — `EMPTY` ("No status") first, then the review
 * workflow (Draft → For action → Reviewed → Approved → Sent). The single
 * ordering constant: every other ordering (sort-rank map, the Action Center
 * progress arc) derives from this, so there is nothing to drift out of sync.
 */
export const SESSION_STATUS_DISPLAY_ORDER: readonly SessionStatus[] = [
  SessionStatus.EMPTY,
  SessionStatus.DRAFT,
  SessionStatus.FOR_ACTION,
  SessionStatus.REVIEWED,
  SessionStatus.APPROVED,
  SessionStatus.SENT,
] as const;

/**
 * Sort-rank map derived from {@link SESSION_STATUS_DISPLAY_ORDER}: each status
 * ranks by its position, except `EMPTY` which sinks to the bottom. Consumed
 * when sorting session lists by status.
 */
export const SESSION_STATUS_SORT_ORDER: Record<SessionStatus, number> =
  SESSION_STATUS_DISPLAY_ORDER.reduce(
    (acc, status, index) => {
      acc[status] =
        status === SessionStatus.EMPTY ? SESSION_STATUS_DISPLAY_ORDER.length : index;
      return acc;
    },
    {} as Record<SessionStatus, number>
  );

/**
 * Comparator behind every session list's "Status" sort, so each list orders
 * identically instead of re-deriving the rank arithmetic locally.
 *
 * A missing status is treated as `EMPTY`, which ranks after every real status:
 * it trails an ascending sort and leads a descending one.
 */
export const compareSessionsByStatus = <
  T extends { session_status?: SessionStatus | null },
>(
  a: T,
  b: T,
  order: 'asc' | 'desc'
): number => {
  const rankA = SESSION_STATUS_SORT_ORDER[a.session_status ?? SessionStatus.EMPTY];
  const rankB = SESSION_STATUS_SORT_ORDER[b.session_status ?? SessionStatus.EMPTY];
  return order === 'asc' ? rankA - rankB : rankB - rankA;
};
